import * as coreClient from "@azure/core-client";
import * as Parameters from "./models/parameters";
import * as Mappers from "./models/mappers";
import {
  DesignerOptionalParams,
  LoginRequest,
  AuthLoginOptionalParams,
  AuthLoginResponse,
  AuthLogoutOptionalParams,
  MediaFilesListOptionalParams,
  MediaFilesListResponse,
  MediaUploadTokenGetOptionalParams,
  MediaUploadTokenGetResponse,
  ThemeSpecGetOptionalParams,
  ThemeSpecGetResponse,
  CreateThemeDraftDto,
  ThemeDraftCreateOptionalParams,
  ThemeDraftCreateResponse,
  ThemeDraftGetOptionalParams,
  ThemeDraftGetResponse,
  CreateThemeDto,
  ThemeCreateOptionalParams,
  ThemeCreateResponse,
  ThemeListOptionalParams,
  ThemeListResponse,
  ThemeGetOptionalParams,
  ThemeGetResponse,
  PublishReqDto,
  ThemePublishOptionalParams,
  ThemePublishResponse,
  ThemeUpdateOptionalParams,
  ThemeUpdateResponse,
  ThemeDeleteOptionalParams,
  ThemeVersionCreateOptionalParams,
  ThemeVersionCreateResponse,
  ThemeVersionUpdateOptionalParams,
  ThemeVersionUpdateResponse,
  ThemeVersionDeleteOptionalParams,
} from "./models";

export class Designer extends coreClient.ServiceClient {
  $host: string;
  xHrSiteId: number;

  /**
   * Initializes a new instance of the Designer class.
   * @param $host server parameter
   * @param xHrSiteId The Site ID
   * @param options The parameter options
   */
  constructor(
    $host: string,
    xHrSiteId: number,
    options?: DesignerOptionalParams,
  ) {
    if ($host === undefined) {
      throw new Error("'$host' cannot be null");
    }
    if (xHrSiteId === undefined) {
      throw new Error("'xHrSiteId' cannot be null");
    }

    // Initializing default values for options
    if (!options) {
      options = {};
    }
    const defaults: DesignerOptionalParams = {
      requestContentType: "application/json; charset=utf-8",
    };

    const packageDetails = `azsdk-js-designer/1.0.0-beta.1`;
    const userAgentPrefix =
      options.userAgentOptions && options.userAgentOptions.userAgentPrefix
        ? `${options.userAgentOptions.userAgentPrefix} ${packageDetails}`
        : `${packageDetails}`;

    const optionsWithDefaults = {
      ...defaults,
      ...options,
      userAgentOptions: {
        userAgentPrefix,
      },
      endpoint: options.endpoint ?? options.baseUri ?? "{$host}",
    };
    super(optionsWithDefaults);
    // Parameter assignments
    this.$host = $host;
    this.xHrSiteId = xHrSiteId;
  }

  /**
   * @param body
   * @param options The options parameters.
   */
  authLogin(
    body: LoginRequest,
    options?: AuthLoginOptionalParams,
  ): Promise<AuthLoginResponse> {
    return this.sendOperationRequest({ body, options }, authLoginOperationSpec);
  }

  /** @param options The options parameters. */
  authLogout(options?: AuthLogoutOptionalParams): Promise<void> {
    return this.sendOperationRequest({ options }, authLogoutOperationSpec);
  }

  /** @param options The options parameters. */
  mediaFilesList(
    options?: MediaFilesListOptionalParams,
  ): Promise<MediaFilesListResponse> {
    return this.sendOperationRequest({ options }, mediaFilesListOperationSpec);
  }

  /**
   * @param filename
   * @param options The options parameters.
   */
  mediaUploadTokenGet(
    filename: string,
    options?: MediaUploadTokenGetOptionalParams,
  ): Promise<MediaUploadTokenGetResponse> {
    return this.sendOperationRequest(
      { filename, options },
      mediaUploadTokenGetOperationSpec,
    );
  }

  /** @param options The options parameters. */
  themeSpecGet(
    options?: ThemeSpecGetOptionalParams,
  ): Promise<ThemeSpecGetResponse> {
    return this.sendOperationRequest({ options }, themeSpecGetOperationSpec);
  }

  /**
   * @param body
   * @param options The options parameters.
   */
  themeDraftCreate(
    body: CreateThemeDraftDto,
    options?: ThemeDraftCreateOptionalParams,
  ): Promise<ThemeDraftCreateResponse> {
    return this.sendOperationRequest(
      { body, options },
      themeDraftCreateOperationSpec,
    );
  }

  /** @param options The options parameters. */
  themeDraftGet(
    options?: ThemeDraftGetOptionalParams,
  ): Promise<ThemeDraftGetResponse> {
    return this.sendOperationRequest({ options }, themeDraftGetOperationSpec);
  }

  /**
   * @param body
   * @param options The options parameters.
   */
  themeCreate(
    body: CreateThemeDto,
    options?: ThemeCreateOptionalParams,
  ): Promise<ThemeCreateResponse> {
    return this.sendOperationRequest(
      { body, options },
      themeCreateOperationSpec,
    );
  }

  /** @param options The options parameters. */
  themeList(options?: ThemeListOptionalParams): Promise<ThemeListResponse> {
    return this.sendOperationRequest({ options }, themeListOperationSpec);
  }

  /**
   * @param id
   * @param options The options parameters.
   */
  themeGet(
    id: string,
    options?: ThemeGetOptionalParams,
  ): Promise<ThemeGetResponse> {
    return this.sendOperationRequest({ id, options }, themeGetOperationSpec);
  }

  /**
   * @param themeId
   * @param body
   * @param options The options parameters.
   */
  themePublish(
    themeId: string,
    body: PublishReqDto,
    options?: ThemePublishOptionalParams,
  ): Promise<ThemePublishResponse> {
    return this.sendOperationRequest(
      { themeId, body, options },
      themePublishOperationSpec,
    );
  }

  /**
   * @param themeId
   * @param body
   * @param options The options parameters.
   */
  themeUpdate(
    themeId: string,
    body: CreateThemeDto,
    options?: ThemeUpdateOptionalParams,
  ): Promise<ThemeUpdateResponse> {
    return this.sendOperationRequest(
      { themeId, body, options },
      themeUpdateOperationSpec,
    );
  }

  /**
   * @param themeId
   * @param options The options parameters.
   */
  themeDelete(
    themeId: string,
    options?: ThemeDeleteOptionalParams,
  ): Promise<void> {
    return this.sendOperationRequest(
      { themeId, options },
      themeDeleteOperationSpec,
    );
  }

  /**
   * @param themeId
   * @param body
   * @param options The options parameters.
   */
  themeVersionCreate(
    themeId: string,
    body: CreateThemeDto,
    options?: ThemeVersionCreateOptionalParams,
  ): Promise<ThemeVersionCreateResponse> {
    return this.sendOperationRequest(
      { themeId, body, options },
      themeVersionCreateOperationSpec,
    );
  }

  /**
   * @param themeId
   * @param versionId
   * @param body
   * @param options The options parameters.
   */
  themeVersionUpdate(
    themeId: string,
    versionId: string,
    body: CreateThemeDto,
    options?: ThemeVersionUpdateOptionalParams,
  ): Promise<ThemeVersionUpdateResponse> {
    return this.sendOperationRequest(
      { themeId, versionId, body, options },
      themeVersionUpdateOperationSpec,
    );
  }

  /**
   * @param themeId
   * @param versionId
   * @param options The options parameters.
   */
  themeVersionDelete(
    themeId: string,
    versionId: string,
    options?: ThemeVersionDeleteOptionalParams,
  ): Promise<void> {
    return this.sendOperationRequest(
      { themeId, versionId, options },
      themeVersionDeleteOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const authLoginOperationSpec: coreClient.OperationSpec = {
  path: "/auth/login",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.UserDto,
    },
  },
  requestBody: Parameters.body,
  urlParameters: [Parameters.$host],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xHrSiteId,
  ],
  mediaType: "json",
  serializer,
};
const authLogoutOperationSpec: coreClient.OperationSpec = {
  path: "/auth/auth/logout",
  httpMethod: "POST",
  responses: { 200: {} },
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.xHrSiteId],
  serializer,
};
const mediaFilesListOperationSpec: coreClient.OperationSpec = {
  path: "/media",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: { type: { name: "Composite", className: "FileMeta" } },
        },
      },
    },
  },
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.xHrSiteId],
  serializer,
};
const mediaUploadTokenGetOperationSpec: coreClient.OperationSpec = {
  path: "/media/{filename}/token",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.BlobUploadToken,
    },
  },
  urlParameters: [Parameters.$host, Parameters.filename],
  headerParameters: [Parameters.accept, Parameters.xHrSiteId],
  serializer,
};
const themeSpecGetOperationSpec: coreClient.OperationSpec = {
  path: "/theme/spec",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeSpec,
    },
  },
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.xHrSiteId],
  serializer,
};
const themeDraftCreateOperationSpec: coreClient.OperationSpec = {
  path: "/theme/draft",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeDraftDto,
    },
  },
  requestBody: Parameters.body1,
  urlParameters: [Parameters.$host],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xHrSiteId,
  ],
  mediaType: "json",
  serializer,
};
const themeDraftGetOperationSpec: coreClient.OperationSpec = {
  path: "/theme/draft",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeVersionDto,
    },
    404: {
      bodyMapper: Mappers.ProblemDetails,
    },
  },
  queryParameters: [Parameters.id],
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.xHrSiteId],
  serializer,
};
const themeCreateOperationSpec: coreClient.OperationSpec = {
  path: "/theme",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeGroupDto,
    },
  },
  requestBody: Parameters.body2,
  urlParameters: [Parameters.$host],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xHrSiteId,
  ],
  mediaType: "json",
  serializer,
};
const themeListOperationSpec: coreClient.OperationSpec = {
  path: "/theme",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: {
        type: {
          name: "Sequence",
          element: { type: { name: "Composite", className: "ThemeGroupDto" } },
        },
      },
    },
    404: {
      bodyMapper: Mappers.ProblemDetails,
    },
  },
  queryParameters: [Parameters.search],
  urlParameters: [Parameters.$host],
  headerParameters: [Parameters.accept, Parameters.xHrSiteId],
  serializer,
};
const themeGetOperationSpec: coreClient.OperationSpec = {
  path: "/theme/{id}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeGroupDto,
    },
    404: {
      bodyMapper: Mappers.ProblemDetails,
    },
  },
  urlParameters: [Parameters.$host, Parameters.id1],
  headerParameters: [Parameters.accept, Parameters.xHrSiteId],
  serializer,
};
const themePublishOperationSpec: coreClient.OperationSpec = {
  path: "/theme/{themeId}/publish",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeVersionDto,
    },
  },
  requestBody: Parameters.body3,
  urlParameters: [Parameters.$host, Parameters.themeId],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xHrSiteId,
  ],
  mediaType: "json",
  serializer,
};
const themeUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/theme/{themeId}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeGroupDto,
    },
  },
  requestBody: Parameters.body2,
  urlParameters: [Parameters.$host, Parameters.themeId],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xHrSiteId,
  ],
  mediaType: "json",
  serializer,
};
const themeDeleteOperationSpec: coreClient.OperationSpec = {
  path: "/theme/{themeId}",
  httpMethod: "DELETE",
  responses: { 200: {} },
  urlParameters: [Parameters.$host, Parameters.themeId],
  headerParameters: [Parameters.xHrSiteId],
  serializer,
};
const themeVersionCreateOperationSpec: coreClient.OperationSpec = {
  path: "/theme/{themeId}/version",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeVersionDto,
    },
  },
  requestBody: Parameters.body2,
  urlParameters: [Parameters.$host, Parameters.themeId],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xHrSiteId,
  ],
  mediaType: "json",
  serializer,
};
const themeVersionUpdateOperationSpec: coreClient.OperationSpec = {
  path: "/theme/{themeId}/version/{versionId}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.ThemeVersionDto,
    },
  },
  requestBody: Parameters.body2,
  urlParameters: [Parameters.$host, Parameters.themeId, Parameters.versionId],
  headerParameters: [
    Parameters.contentType,
    Parameters.accept,
    Parameters.xHrSiteId,
  ],
  mediaType: "json",
  serializer,
};
const themeVersionDeleteOperationSpec: coreClient.OperationSpec = {
  path: "/theme/{themeId}/version/{versionId}",
  httpMethod: "DELETE",
  responses: { 200: {} },
  urlParameters: [Parameters.$host, Parameters.themeId, Parameters.versionId],
  headerParameters: [Parameters.xHrSiteId],
  serializer,
};
